import { isLiveApiFlag } from "../apiMode";
import { API_ENDPOINTS, apiService } from "../api";
import { kgWrite, kgWritePath } from "../kg/kgWritePath";
import type {
  CreateDataModelRequest,
  CreateDataModelResponse,
  DeleteDataModelResponse,
  GetDataModelBuilderResponse,
  UpdateDataModelRequest,
  UpdateDataModelResponse,
} from "./dataModelBuilder.types";
import {
  mockCreateDataModel,
  mockDeleteDataModel,
  mockFetchDataModelBuilder,
  mockUpdateDataModel,
} from "./dataModelBuilder.mock";
import { mapLiveCatalogResponse } from "./dataModelCatalogApiMappers";
import type { GetDataModelCatalogApiResponse } from "./dataModelCatalogApi.types";
import type {
  GetDataModelCatalogQuery,
  GetDataModelCatalogResponse,
  GetTableDetailsResponse,
} from "./dataModelCatalog.types";
import {
  mockFetchDataModelCatalog,
  mockFetchTableDetails,
} from "./dataModelCatalog.mock.fetch";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";
import { mockFetchDataModels } from "./dataModelsList.mock";
import type { GetDataModelsQuery, GetDataModelsResponse } from "./dataModels.types";
import { buildCatalogUrl, buildListUrl } from "./dataModelsUrlBuilders";

const isGlobalDataModelsApiLive = () =>
  isLiveApiFlag(process.env.REACT_APP_DATA_MODELS_API);

export const isListApiLive = (): boolean =>
  isGlobalDataModelsApiLive() ||
  isLiveApiFlag(process.env.REACT_APP_DATA_MODELS_LIST_API);

export const isCatalogApiLive = (): boolean =>
  isGlobalDataModelsApiLive() ||
  isLiveApiFlag(process.env.REACT_APP_DATA_MODELS_CATALOG_API);

const isOtherDataModelsApiLive = (): boolean => isGlobalDataModelsApiLive();

/** Builder, save, update, delete, and table detail endpoints. */
export const isDataModelBuilderApiLive = (): boolean =>
  isOtherDataModelsApiLive();

export const fetchDataModels = async (
  query?: GetDataModelsQuery,
  workspaceId: string = getActiveWorkspaceId(),
): Promise<GetDataModelsResponse> => {
  if (!isListApiLive()) {
    return mockFetchDataModels(query);
  }
  return apiService.get<GetDataModelsResponse>(buildListUrl(workspaceId, query));
};

export const fetchDataModelCatalog = async (
  query?: GetDataModelCatalogQuery,
  workspaceId: string = getActiveWorkspaceId(),
): Promise<GetDataModelCatalogResponse> => {
  if (!isCatalogApiLive()) {
    return mockFetchDataModelCatalog(query);
  }
  const response = await apiService.get<GetDataModelCatalogApiResponse>(
    buildCatalogUrl(workspaceId, query),
  );
  return mapLiveCatalogResponse(response);
};

export const fetchDataModelBuilder = async (
  dataModelId: string,
  workspaceId: string = getActiveWorkspaceId(),
): Promise<GetDataModelBuilderResponse> => {
  if (!isOtherDataModelsApiLive()) {
    return mockFetchDataModelBuilder(dataModelId);
  }
  return apiService.get<GetDataModelBuilderResponse>(
    API_ENDPOINTS.dataModels.builder(workspaceId, dataModelId),
  );
};

export const fetchTableDetails = async (
  tableId: string,
  workspaceId: string = getActiveWorkspaceId(),
): Promise<GetTableDetailsResponse> => {
  if (!isOtherDataModelsApiLive()) {
    return mockFetchTableDetails(tableId);
  }
  return apiService.get<GetTableDetailsResponse>(
    API_ENDPOINTS.dataModels.table(workspaceId, tableId),
  );
};

export const createDataModel = async (
  body: CreateDataModelRequest,
  workspaceId: string = getActiveWorkspaceId(),
): Promise<CreateDataModelResponse> => {
  if (!isOtherDataModelsApiLive()) {
    return mockCreateDataModel(body);
  }
  const path = await kgWritePath(API_ENDPOINTS.dataModels.list(workspaceId));
  return kgWrite(apiService.post<CreateDataModelResponse>(path, body), () =>
    savedDataModel("", body, 1),
  );
};

export const updateDataModel = async (
  dataModelId: string,
  body: UpdateDataModelRequest,
  workspaceId: string = getActiveWorkspaceId(),
): Promise<UpdateDataModelResponse> => {
  if (!isOtherDataModelsApiLive()) {
    return mockUpdateDataModel(dataModelId, body);
  }
  const path = await kgWritePath(API_ENDPOINTS.dataModels.byId(workspaceId, dataModelId));
  return kgWrite(apiService.patch<UpdateDataModelResponse>(path, body), () =>
    savedDataModel(dataModelId, body, body.version + 1),
  );
};

/** A data model as saved while the twin still applies it: what was sent. */
const savedDataModel = (
  id: string,
  body: CreateDataModelRequest,
  version: number,
): CreateDataModelResponse => {
  const now = new Date().toISOString();
  return {
    data: {
      id,
      name: body.name,
      description: body.description,
      status: body.status,
      version,
      tablesCount: body.tables.length,
      metricsCount: body.metricIds.length,
      createdAt: now,
      updatedAt: now,
    },
  };
};

export const deleteDataModel = async (
  dataModelId: string,
  workspaceId: string = getActiveWorkspaceId(),
): Promise<DeleteDataModelResponse> => {
  if (!isOtherDataModelsApiLive()) {
    return mockDeleteDataModel(dataModelId);
  }
  return apiService.delete<DeleteDataModelResponse>(
    await kgWritePath(API_ENDPOINTS.dataModels.byId(workspaceId, dataModelId)),
  );
};
